import { useQuery } from "@tanstack/react-query";
import { NAV_FALLBACK_ITEMS, type NavItem } from "@dramapie/shared";
import { useEffect, useId, useRef, useState } from "react";

import { getNav } from "../lib/api";

export interface ProductSwitcherProps {
  /**
   * 当前站点 host，缺省 `window.location.host`。
   * 逐字比较（含端口）：测试跨站标记时注入，生产不传。
   */
  currentHost?: string;
  /** 直接给菜单（测试 / 宿主注入），缺省请求 `GET /api/nav`。 */
  items?: readonly NavItem[];
  /** `rail`（左轨 11–12px）| `brand`（顶栏品牌字，跟原 wordmark 同字号）。 */
  variant?: "rail" | "brand";
}

function hostOfWindow(): string {
  return typeof window === "undefined" ? "" : window.location.host;
}

/**
 * 跨站产品切换（P1-W3/W4）：工作室 / 画布 / 账户。
 *
 * 位置与形态刻意跟现有 `ProjectSwitcher` 一致：触发器 + 绝对定位的菜单、
 * Esc / 点外关闭、焦点回到触发器。这里额外补了 ↑↓ 在菜单项之间移动 ——
 * 它是全站导航，键盘必须能走完。
 *
 * 链接是**整页跳转**（不同站点，不是 SPA 路由），所以用 `<a href>`；
 * 当前站点按 `item.host === location.host` 标勾 + 琥珀色。
 */
export function ProductSwitcher({ currentHost, items, variant = "rail" }: ProductSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const menuId = useId();
  const host = currentHost ?? hostOfWindow();

  // 后端保证有兜底；placeholderData 让首帧就有三条，断网也不会空着。
  const navQuery = useQuery({
    queryKey: ["nav"],
    queryFn: async (): Promise<readonly NavItem[]> => (await getNav()).items,
    enabled: items === undefined,
    placeholderData: NAV_FALLBACK_ITEMS,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const list: readonly NavItem[] = items ?? navQuery.data ?? NAV_FALLBACK_ITEMS;

  // 左轨是 overflow:auto，菜单比它宽；fixed 定位才不会被裁掉（同 OutputMenu）。
  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 220;
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
      setPosition({ left, top: rect.bottom + 6 });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  // 打开后：焦点进第一条；Esc 关闭并还给触发器；点外面关闭。
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      itemRefs.current[0]?.focus();
    }, 0);

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      // 触发器自己负责 toggle，别在 mousedown 阶段抢先关掉。
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  function focusItem(delta: number): void {
    const nodes = itemRefs.current.filter((node): node is HTMLAnchorElement => node !== null);
    if (nodes.length === 0) return;
    const current = nodes.indexOf(document.activeElement as HTMLAnchorElement);
    const next =
      current === -1
        ? delta > 0
          ? 0
          : nodes.length - 1
        : (current + delta + nodes.length) % nodes.length;
    nodes[next]!.focus();
  }

  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItem(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItem(-1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      itemRefs.current.find((node): node is HTMLAnchorElement => node !== null)?.focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      [...itemRefs.current].reverse().find((node): node is HTMLAnchorElement => node !== null)?.focus();
    }
  }

  return (
    <div className={`dp-product dp-product-${variant}`}>
      <button
        ref={triggerRef}
        type="button"
        className="dp-product-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        {...(open ? { "aria-controls": menuId } : {})}
        title="切换产品：工作室 / 画布 / 账户"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="dp-product-name">Drama Pie</span>
        <span className="dp-product-caret" aria-hidden="true">
          ⌄
        </span>
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="切换产品"
          className="dp-product-menu"
          style={position ? { left: position.left, top: position.top } : undefined}
          onKeyDown={onMenuKeyDown}
        >
          <ul className="dp-product-list" role="none">
            {list.map((item, index) => {
              const current = host.length > 0 && item.host === host;
              return (
                <li key={item.id} role="none">
                  <a
                    ref={(node) => {
                      itemRefs.current[index] = node;
                    }}
                    role="menuitem"
                    href={item.url}
                    className={`dp-product-item${current ? " is-current" : ""}`}
                    aria-current={current ? "page" : undefined}
                    onClick={() => setOpen(false)}
                  >
                    <span className="dp-product-item-text">
                      <b>{item.name}</b>
                      <em>{item.host}</em>
                    </span>
                    {current ? (
                      <span className="dp-product-check" aria-hidden="true">
                        ✓
                      </span>
                    ) : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
